import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@wbk/ui';
import { type WebSection } from '@wbk/contentful/api';
import { useRouter } from '@wbk/config';

import useExploreContext from '@/context/explore/useExplore';
import ScrollTitle from '@/components/ui/ScrollTitle';
import EventCardItem from '@/components/events/EventCardItem';
import AnimatedLogoColored from '@/components/ui/spinners/AnimatedLogoColored';
import useExploreFetch from '@/hooks/use-explore-fetch';

export default function EventSectionWithPagination({ section }: { section: WebSection }) {
  const { t } = useTranslation('common');
  const { targetRef } = useExploreContext();

  const { push, pathname } = useRouter();

  const {
    allEvents,
    loading,
    fetchMoreEvents,
    fetchMoreExperiences,
    fetchMoreRestaurants,
    fetchMoreZones,
    hasMoreExperiences,
    hasMoreEvents,
    hasMoreRestaurants,
    hasMoreZones,
  } = useExploreFetch({});

  const items = allEvents || [];

  function handleLoadMore() {
    if (hasMoreEvents) {
      fetchMoreEvents();
    }
    if (hasMoreExperiences) {
      fetchMoreExperiences();
    }

    if (hasMoreRestaurants) {
      fetchMoreRestaurants();
    }

    if (hasMoreZones) {
      fetchMoreZones();
    }
  }

  const [searchParams] = useSearchParams();
  const search = searchParams.get('q') || '';
  useEffect(() => {
    if (search && targetRef) {
      targetRef?.current?.scrollIntoView?.({ behavior: 'smooth' });
    }
  }, [search, targetRef]);

  const hasMore = hasMoreEvents || hasMoreExperiences || hasMoreRestaurants || hasMoreZones;

  return (
    <div className='container-padding container'>
      <section ref={targetRef} className='container space-y-6 px-3 md:space-y-12 xl:px-0'>
        <div className='flex items-center justify-between'>
          <ScrollTitle title={section.title} />
        </div>

        {loading && items.length === 0 ? (
          <div className='flex h-96 w-full items-center justify-center'>
            <AnimatedLogoColored />
          </div>
        ) : (
          <>
            {items.length === 0 ? (
              <div className='flex h-96 flex-col items-center justify-center gap-6'>
                <div>
                  <Typography variant='body-L' className='text-center'>
                    {t('errors.could_not_find_what_you_are_looking_for')}
                  </Typography>
                  <Typography className='text-center text-[#A1A1AA]'>
                    {t('errors.try_adjusting_or_removing_the_filters')}
                  </Typography>
                </div>

                <Button
                  onClick={() => {
                    push(pathname);
                    targetRef?.current?.scrollIntoView?.({ behavior: 'smooth' });
                  }}
                  className='w-full shrink-0 rounded-full bg-white/20 px-6 py-1 hover:bg-white/10 xl:w-auto'
                >
                  {t('reset')}
                  <span>
                    <svg
                      width='14'
                      height='14'
                      viewBox='0 0 11 11'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M2.55078 0.795532C2.6461 0.395922 3.15579 0.274885 3.41992 0.589478L3.69629 0.919556C4.32737 0.650903 5.02083 0.499634 5.75 0.499634C8.64939 0.499634 10.9998 2.85028 11 5.74963C11 8.64913 8.6495 10.9996 5.75 10.9996C2.85051 10.9996 0.5 8.64913 0.5 5.74963C0.500028 5.25731 0.567593 4.78058 0.694336 4.32874L1.65723 4.59924C1.55325 4.97 1.50003 5.35578 1.5 5.74963C1.5 8.09684 3.40279 9.99963 5.75 9.99963C8.09721 9.99963 10 8.09684 10 5.74963C9.99983 3.40257 8.09711 1.49963 5.75 1.49963C5.27469 1.49963 4.81255 1.58005 4.37598 1.72913L4.64941 2.05432C4.91396 2.3696 4.70485 2.85232 4.29395 2.87561L2.69434 2.96545C2.36023 2.98439 2.10232 2.6767 2.17969 2.3512L2.55078 0.795532Z'
                        fill='#A1A1AA'
                      />
                    </svg>
                  </span>
                </Button>
              </div>
            ) : (
              <>
                <div className='hidden lg:block lg:space-y-12'>
                  <div className='space-y-8'>
                    <div className='grid grid-cols-3 gap-4'>
                      {(items ?? []).slice(0, 3).map((item, index) => {
                        return (
                          <EventCardItem item={item} index={index} section={section} key={index} />
                        );
                      })}
                    </div>
                    <div className='grid grid-cols-4 gap-4'>
                      {items.slice(3).map((item, index) => {
                        return (
                          <EventCardItem item={item} index={index} section={section} key={index} />
                        );
                      })}
                    </div>

                    {hasMore && (
                      <div className='flex'>
                        <Button
                          phill
                          onClick={handleLoadMore}
                          loading={loading}
                          className='mx-auto flex items-center border border-white bg-transparent px-8 capitalize'
                        >
                          <Typography variant='body-L' className='font-semibold'>
                            {t('load_more')}
                          </Typography>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className='space-y-6 md:space-y-12 lg:hidden'>
                  {items.slice(0, 1).map((item, index) => {
                    return (
                      <EventCardItem item={item} index={index} section={section} key={index} />
                    );
                  })}
                  <div className='grid grid-cols-2 gap-4'>
                    {items.slice(1).map((item, index) => {
                      return (
                        <EventCardItem item={item} index={index} section={section} key={index} />
                      );
                    })}
                  </div>

                  {hasMore && (
                    <div className='flex'>
                      <Button
                        phill
                        onClick={handleLoadMore}
                        loading={loading}
                        className='mx-auto flex items-center border border-white bg-transparent px-4'
                      >
                        <Typography variant='body-S' className='sm:text-body-L font-semibold'>
                          {t('load_more')}
                        </Typography>
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
